import { useDebounce } from '@lib/common';
import { getUseCase, RequestStatus } from '@lib/plugin-redux-core';
import {
  Form,
  FormSummary,
  FolderSummary,
  SEARCH_SPACE_OBJECTS_USE_CASE,
  SearchQueryParams,
} from '@module/form';
import { FileOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { Col, Row, Spin } from 'antd';
import moment from 'moment';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { RootReducerState } from '../../../redux/root.reducer';
import { SpacePageState } from '../../../redux/space';
import { openNewTab } from '../../../utils';
import { buildFormEditUrl } from 'utils';
import {
  buildSpaceUrl,
  readSpaceFolderTrailFromQuery,
  resolveSpaceFolderTrail,
} from '../../../utils/space-url.util';

const StyledColItem = styled(Col)`
  text-align: end;
`;

const StyledItemContainer = styled(Row)`
  .anticon {
    margin-right: 10px;
  }
`;

const StyledSpin = styled(Spin)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const useSpaceSearch = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const router = useRouter();
  const handleDebounce = useDebounce(250);

  const {
    formsAndFoldersSearchOptions: searchOptions,
    searchFormsAndFoldersStatus,
  }: Pick<SpacePageState, 'formsAndFoldersSearchOptions' | 'searchFormsAndFoldersStatus'> =
    useSelector((state: RootReducerState) => state.spaceObjects, shallowEqual);

  const searchUseCase = useMemo(
    () => getUseCase(SEARCH_SPACE_OBJECTS_USE_CASE, dispatch),
    [dispatch],
  );

  const handleSearchSpaceObjects = useCallback(
    (searchText: string): void => {
      handleDebounce(() => {
        const searchFormAndFolderQueryParams: SearchQueryParams = {
          search: searchText,
        };
        searchUseCase.execute(searchFormAndFolderQueryParams);
      });
    },
    [handleDebounce, searchUseCase],
  );

  const handleFolderDetailClick = useCallback(
    (folderId: string, folderTitle?: string): void => {
      const currentTrail =
        searchOptions?.parentFolders?.map((folder) => ({
          id: folder.id.toString(),
          title: folder.title,
        })) ?? [];

      const trail = resolveSpaceFolderTrail({
        currentTrail,
        targetFolderId: folderId,
        targetFolderTitle:
          folderTitle ||
          searchOptions?.folders?.find((folder) => folder.id.toString() === folderId)?.title,
      });

      const url = trail
        ? buildSpaceUrl({
            folderId: trail.folderId,
            folderIds: trail.folderIds,
            folderNames: trail.folderNames,
          })
        : buildSpaceUrl({ folderId });

      router.push(url);
    },
    [router, searchOptions],
  );

  const handleFormDetailClick = useCallback(
    (form: FormSummary | Form): void => {
      const trailFromQuery = readSpaceFolderTrailFromQuery(router.query);
      const url = buildFormEditUrl(form.id.toString(), form.title, {
        folderId: trailFromQuery?.folderId ?? (router.query.folder as string),
        folderIds: trailFromQuery?.folderIds,
        folderNames: trailFromQuery?.folderNames,
      });
      openNewTab(url);
    },
    [router.query],
  );

  const options = useMemo(() => {
    const isLoading = searchFormsAndFoldersStatus === RequestStatus.EXECUTE;
    if (isLoading) {
      return [{ label: <StyledSpin size="small" /> }];
    }

    if (!searchOptions) {
      return [];
    }

    const isEmpty =
      (searchOptions.forms?.length ?? 0) === 0 && (searchOptions.folders?.length ?? 0) === 0;
    if (isEmpty) {
      return [{ label: <i>{t('noResults')}</i> }];
    }

    const optionsArray: { label: string; options?: unknown[] }[] = [];

    if (searchOptions.forms && searchOptions.forms.length > 0) {
      optionsArray.push({
        label: t('form'),
        options: searchOptions.forms.map((item: FormSummary) => ({
          key: item.id,
          value: item.title,
          label: (
            <StyledItemContainer onClick={() => handleFormDetailClick(item)}>
              <Col span={14} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <FileOutlined /> {item.title}
              </Col>
              <StyledColItem span={10}>
                {moment.unix(+item.updatedAt / 1000).format('DD/MM')}
              </StyledColItem>
            </StyledItemContainer>
          ),
        })),
      });
    }

    if (searchOptions.folders && searchOptions.folders.length > 0) {
      optionsArray.push({
        label: t('folder'),
        options: searchOptions.folders.map((item: FolderSummary) => ({
          key: item.id,
          value: item.title,
          label: (
            <StyledItemContainer
              onClick={() => handleFolderDetailClick(item.id.toString(), item.title)}
            >
              <Col span={14} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <FolderOpenOutlined /> {item.title}
              </Col>
              <StyledColItem span={10}>
                {moment.unix(+item.updatedAt / 1000).format('DD/MM')}
              </StyledColItem>
            </StyledItemContainer>
          ),
        })),
      });
    }

    return optionsArray;
  }, [
    searchOptions,
    searchFormsAndFoldersStatus,
    t,
    handleFormDetailClick,
    handleFolderDetailClick,
  ]);

  return {
    t,
    options,
    handleSearchSpaceObjects,
  };
};
